import { Injectable } from "@angular/core";
import { PermissionService } from "./permission.service";
import { SessionService } from "./session.service";
import { Features } from "../backend/auth/Feature";
import { Actions } from "../backend/auth/Action";

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  isSuperAdmin = false;

  constructor(
    public permissionService: PermissionService,
    public session: SessionService
  ) {
    this.session.isSuperAdmin$().subscribe((isSuperAdmin) => {
      this.isSuperAdmin = isSuperAdmin;
    });
  }

  async getUserMenus(): Promise<Menu[]> {
    // Prevent redundant API calls: preload and cache (settings + permission) once
    await this.permissionService.warmUp();

    const company = await this.session.getCompanyData();
    const implicitSaleCreation = !!company?.implicitSaleCreation;

    const result: Menu[] = [];

    for (const menu of this.getMenus(implicitSaleCreation)) {
      const hasChildren = (menu.children?.length ?? 0) > 0;
      if (hasChildren) {
        // Parent: no feature/permission check
        const filteredChildren: Menu[] = [];

        for (const child of menu.children!) {
          const childHasFeature = child.feature ? await this.permissionService.hasFeaturePermissions(child.feature) : true;
          const childHasPermission =
            (child.actions
              ? await this.permissionService.hasActionPermissions(child.actions)
              : true
          );

          if (childHasFeature && childHasPermission) {
            filteredChildren.push(child);
          }
        }

        if (filteredChildren.length > 0) {
          result.push({ ...menu, children: filteredChildren });
        }
      } else {
        const hasFeature = menu.feature ? await this.permissionService.hasFeaturePermissions(menu.feature) : true;
        const hasPermission =
          (menu.actions
            ? await this.permissionService.hasActionPermissions(menu.actions)
            : true
          );

        if (hasFeature && hasPermission) {
          result.push(menu);
        }
      }
    }
    return result;
  }

  getMenus(implicitSaleCreation: boolean) {

    const warrantyActivationPath = implicitSaleCreation ? '/sales/add' : '/warranties/activate';

    return [

      // Dashboard
      new Menu('CORE_SIDEBAR.DASHBOARD', '/dashboard', 'icon icon-dashboard', true, Features.Dashboards, [Actions.view_dashboards]),

      // Sales
      new Menu('CORE_SIDEBAR.SALES', '/sales', 'icon icon-sales', false, Features.Sales, [Actions.view_sales, Actions.create_sales, Actions.modify_sales, Actions.return_sales, Actions.export_sales]),

      // Warranties
      new Menu('CORE_SIDEBAR.WARRANTIES', '', 'icon icon-warranties', false, null, null, [
        new Menu('CORE_SIDEBAR.WARRANTIES_LIST', '/warranties', 'icon icon-warranties', false, Features.Warranties, [Actions.view_warranties, Actions.activate_warranties, Actions.extend_warranties, Actions.void_warranties]),
        new Menu('CORE_SIDEBAR.WARRANTY_ACTIVATION', warrantyActivationPath, 'icon icon-warranty-activation', false, Features.Warranties, [Actions.activate_warranties]),
      ], true),

      // Inventory and Logistics Group
      new Menu('CORE_SIDEBAR.INVENTORY_AND_LOGISTICS', '', 'icon icon-inventory', false, null, null, [
        new Menu('CORE_SIDEBAR.INVENTORIES', '/inventories', 'icon icon-inventory', false, Features.Inventories, [Actions.view_inventories, Actions.create_inventories, Actions.modify_inventories, Actions.retailer_inventories, Actions.export_inventories]),
        new Menu('CORE_SIDEBAR.ORDERS', '/code-orders', 'icon icon-orders', false, Features.SerialisationEngine, [Actions.view_orders, Actions.create_orders, Actions.download_orders]),
        new Menu('CORE_SIDEBAR.SHIPMENT', '/shipments', 'icon icon-shipment', false, Features.Shipments, [Actions.view_shipments, Actions.create_shipments, Actions.modify_shipments, Actions.export_shipments])
      ]),

      // Retailers Group
      new Menu('CORE_SIDEBAR.RETAILERS', '', 'icon icon-retailers', false, null, null, [
        new Menu('CORE_SIDEBAR.RETAILERS_LIST', '/retailers', 'icon icon-retailers', false, Features.Retailers, [Actions.view_retailers, Actions.create_retailers, Actions.modify_retailers, Actions.manage_retailers_users, Actions.export_retailers]),

        new Menu('CORE_SIDEBAR.RETAILER_ATTRIBUTE_DEFINITIONS', '/retailer-attribute-definitions', 'icon icon-retailer-attributes', false, Features.RetailerAttributes, [Actions.view_retailerattributedefinitions, Actions.create_retailerattributedefinitions, Actions.modify_retailerattributedefinitions]),

        new Menu('CORE_SIDEBAR.RETAILER_TYPES', '/retailer-types', 'icon icon-retailer-type', false, Features.Retailers, [Actions.view_retailertypes, Actions.create_retailertypes, Actions.modify_retailertypes]),
        new Menu('CORE_SIDEBAR.BUSINESS_GROUPS', '/business-groups', 'icon icon-business-group', false, Features.BusinessGroups, [Actions.view_business_groups, Actions.create_business_groups, Actions.modify_business_groups]),
        new Menu('CORE_SIDEBAR.DISTRIBUTION_CHANNELS', '/distribution-channels', 'icon icon-distribution-channel', false, Features.DistributionChannels, [Actions.view_distributionchannels, Actions.create_distributionchannels, Actions.modify_distributionchannels]),
        new Menu('CORE_SIDEBAR.MARKETS', '/markets', 'icon icon-markets', false, Features.Retailers, [Actions.view_markets, Actions.create_markets, Actions.modify_markets, Actions.manage_market_users, Actions.manage_market_retailers])
      ]),

      // Users
      new Menu('CORE_SIDEBAR.USERS', '/users', 'icon icon-users', false, Features.Basic, [Actions.view_users, Actions.create_users, Actions.modify_users, Actions.export_users]),

      // Unregistered Users
      new Menu('CORE_SIDEBAR.UNREGISTERED_USERS', '/unregistered-users', 'icon icon-unregistered-users', false, Features.Sellers, [Actions.view_users, Actions.create_users, Actions.modify_users, Actions.export_users]),

      // Product Management Group
      new Menu('CORE_SIDEBAR.PRODUCT_MANAGEMENT', '', 'icon icon-products', false, null, null, [
        new Menu('CORE_SIDEBAR.PRODUCTS', '/products', 'icon icon-products', false, Features.Products, [Actions.view_products, Actions.create_products, Actions.modify_products]),
        new Menu('CORE_SIDEBAR.PRODUCT_CUSTOMIZATIONS', '/product-customisations', 'icon icon-product-customisations', false, Features.ProductCustomisations, [Actions.view_productcustomisations, Actions.create_productcustomisations, Actions.modify_productcustomisations]),
        new Menu('CORE_SIDEBAR.PRODUCT_ATTRIBUTE_DEFINITIONS', '/product-attribute-definitions', 'icon icon-product-attributes', false, Features.ProductAttributes, [Actions.view_productattributedefinitions, Actions.create_productattributedefinitions, Actions.modify_productattributedefinitions]),
        new Menu('CORE_SIDEBAR.PRODUCT_TYPES', '/product-types', 'icon icon-product-types', false, Features.Products, [Actions.view_producttypes, Actions.create_producttypes, Actions.modify_producttypes]),
        new Menu('CORE_SIDEBAR.CATEGORIES', '/categories', 'icon icon-categories', false, Features.Categories, [Actions.view_categories, Actions.create_categories, Actions.modify_categories]),
        new Menu('CORE_SIDEBAR.COLLECTIONS', '/collections', 'icon icon-collections', false, Features.Collections, [Actions.view_collections, Actions.create_collections, Actions.modify_collections]),
        new Menu('CORE_SIDEBAR.ITEMS', '/items', 'icon icon-items', false, Features.Products, [Actions.view_items, Actions.create_items, Actions.modify_items, Actions.recovered_items, Actions.stolen_items, Actions.export_items])
      ]),

      // Repair
      new Menu('CORE_SIDEBAR.REPAIR', '/repair', 'icon icon-repair', false, Features.Repair, [Actions.repair]),

      // Customers
      new Menu('CORE_SIDEBAR.CUSTOMERS', '/customers', 'icon icon-customers', false, Features.Customers, [Actions.view_customers, Actions.create_customers, Actions.modify_customers, Actions.export_customers]),

      // Surveys
      new Menu('CORE_SIDEBAR.SURVEYS', '/surveys', 'icon icon-surveys', false, Features.Customers, [Actions.view_surveys, Actions.create_surveys, Actions.modify_surveys]),

      // Digital Card
      new Menu('CORE_SIDEBAR.DIGITAL_CARD', '/digital-card/add', 'icon icon-digital-cards', false, Features.DigitalCards, [Actions.digitalcards]),

      // Order cards
      new Menu('CORE_SIDEBAR.CARD_ORDERING', '/card-orders', 'icon icon-card-ordering', false, Features.CardOrdering, [Actions.view_cardorders, Actions.create_cardorders, Actions.export_cardorders]),

      // Data Operations Group
      new Menu('CORE_SIDEBAR.DATA_OPERATIONS', '', 'icon icon-import', false, null, null, [
        new Menu('CORE_SIDEBAR.IMPORTS', '/imports', 'icon icon-import', false, Features.Imports, [Actions.view_imports, Actions.create_imports, Actions.import_cardorderings, Actions.import_download, Actions.import_inventories, Actions.import_items, Actions.import_retailers, Actions.import_retailers, Actions.import_shipments]),
        new Menu('CORE_SIDEBAR.EXPORTS', '/exports', 'icon icon-export', false, Features.Exports, [Actions.view_exports])
      ])
    ];
  }
}

export class Menu {
  constructor(
    public text: string,
    public path: string,
    public icon: string,
    public isHeader: boolean,
    public feature?: Features | null,
    public actions?: Actions[] | null,
    public children: Menu[] = [],
    public isExpanded?: boolean,
  ) { }
}
